/**
 * ArduPilot / PX4 bootloader client, over Web Serial.
 *
 * This is the same protocol `px_uploader.py` (ArduPilot) and Mission Planner
 * speak: the board's bootloader enumerates as a USB CDC serial device and takes
 * a small command set — sync, identify, erase, program, CRC, reboot. Every
 * command is `[CMD, ...args, EOC]` and every reply ends `[INSYNC, OK]`.
 *
 * Deliberately NOT here (see the flasher page for what this costs the user):
 *  - USB DFU / WebUSB. That is the recovery path for a board with no
 *    bootloader, and on Windows it needs a Zadig WinUSB driver swap.
 *  - Auto "reboot into bootloader". Real boards need MAVLink or NSH magic and
 *    then re-enumerate as a *new* USB device, which invalidates the SerialPort
 *    handle. For now the user power-cycles and we catch the bootloader window.
 *
 * ponytail: bootloader rev >= 3 only (GET_CRC). Revs 1-2 need the much slower
 * READ_MULTI verify; every ArduPilot board since ~2016 is rev 4/5. Add it if a
 * genuinely old board turns up.
 * ponytail: no external-flash (EXTF_*) support, so boards that keep firmware in
 * external QSPI flash will fail the size check rather than flash wrongly.
 */

const P = {
  INSYNC: 0x12,
  EOC: 0x20,
  OK: 0x10,
  FAILED: 0x11,
  INVALID: 0x13,
  BAD_SILICON_REV: 0x14,
  GET_SYNC: 0x21,
  GET_DEVICE: 0x22,
  CHIP_ERASE: 0x23,
  PROG_MULTI: 0x27,
  GET_CRC: 0x29,
  REBOOT: 0x30,
};

const INFO = { BL_REV: 1, BOARD_ID: 2, BOARD_REV: 3, FLASH_SIZE: 4 };

// Upstream px_uploader's PROG_MULTI_MAX. The length is a single byte and the
// bootloader's receive buffer is 256, so this is a hard ceiling, not a tuning knob.
const PROG_MULTI_MAX = 252;

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

/** zlib-compatible CRC32, with a resumable seed (matches Python's `binascii.crc32`). */
export function crc32(bytes, seed = 0) {
  let c = (~seed) >>> 0;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (~c) >>> 0;
}

/**
 * The CRC the bootloader will report for `image`, given it CRCs the whole flash
 * bank with the unwritten tail reading 0xFF. Mirrors `firmware.crc()` in
 * px_uploader.py exactly, including the `padLen - 1` bound — get this wrong and
 * every flash "fails verification" after writing perfectly good firmware.
 */
export function firmwareCrc(image, padLen) {
  let state = crc32(image, 0);
  const FF4 = new Uint8Array([0xff, 0xff, 0xff, 0xff]);
  for (let i = image.length; i < padLen - 1; i += 4) state = crc32(FF4, state);
  return state;
}

/**
 * Parse an ArduPilot `.apj` (JSON envelope: base64 of a zlib-compressed image).
 * Uses the platform's DecompressionStream rather than bundling an inflate.
 */
export async function parseApj(text) {
  let apj;
  try {
    apj = JSON.parse(text);
  } catch {
    throw new Error("Not a valid .apj file — expected JSON.");
  }
  if (typeof apj.image !== "string") {
    throw new Error("Not an ArduPilot .apj file (no `image` field).");
  }

  const packed = Uint8Array.from(atob(apj.image), (ch) => ch.charCodeAt(0));
  const stream = new Blob([packed]).stream().pipeThrough(new DecompressionStream("deflate"));
  let image = new Uint8Array(await new Response(stream).arrayBuffer());

  if (apj.image_size && image.length !== apj.image_size) {
    throw new Error(
      `Corrupt .apj: decompressed to ${image.length} bytes, header says ${apj.image_size}.`,
    );
  }
  // The bootloader programs in 4-byte words; pad the tail the way flash reads.
  if (image.length % 4) {
    const padded = new Uint8Array(image.length + 4 - (image.length % 4)).fill(0xff);
    padded.set(image);
    image = padded;
  }

  return {
    image,
    boardId: apj.board_id,
    summary: apj.summary || "",
    description: apj.description || "",
    version: apj.version || "",
    gitIdentity: apj.git_identity || "",
  };
}

/** A raw `.bin` — no board ID to check against, so the caller must warn. */
export function parseBin(buffer) {
  let image = new Uint8Array(buffer);
  if (image.length % 4) {
    const padded = new Uint8Array(image.length + 4 - (image.length % 4)).fill(0xff);
    padded.set(image);
    image = padded;
  }
  return { image, boardId: null, summary: "", description: "", version: "", gitIdentity: "" };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Buffered, timeout-aware framing over a Web Serial port. */
class Link {
  constructor(port) {
    this.port = port;
    this.buf = new Uint8Array(0);
    this.pending = null;
    this.closed = false;
  }

  async open(baudRate = 115200) {
    await this.port.open({ baudRate });
    this.reader = this.port.readable.getReader();
    this.writer = this.port.writable.getWriter();
  }

  async close() {
    try {
      await this.reader?.cancel();
      this.reader?.releaseLock();
      this.writer?.releaseLock();
      await this.port.close();
    } catch {
      // Port already gone (the board reboots out from under us on success).
    }
  }

  // A read that loses its race with a timeout must still land its bytes in the
  // buffer, or the next command reads a truncated frame and desyncs.
  pump() {
    if (!this.pending) {
      this.pending = this.reader.read().then(({ value, done }) => {
        this.pending = null;
        if (done) {
          this.closed = true;
          return;
        }
        if (value?.length) {
          const next = new Uint8Array(this.buf.length + value.length);
          next.set(this.buf);
          next.set(value, this.buf.length);
          this.buf = next;
        }
      });
    }
    return this.pending;
  }

  async send(bytes) {
    await this.writer.write(Uint8Array.from(bytes));
  }

  async recv(n, timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    while (this.buf.length < n) {
      if (this.closed) throw new Error("The board disconnected mid-transfer.");
      const left = deadline - Date.now();
      if (left <= 0) throw new Error(`Timed out waiting for ${n} byte(s) from the bootloader.`);
      await Promise.race([this.pump(), sleep(left)]);
    }
    const out = this.buf.slice(0, n);
    this.buf = this.buf.slice(n);
    return out;
  }

  flush() {
    this.buf = new Uint8Array(0);
  }
}

async function expectSync(link, timeoutMs = 2000) {
  const [a, b] = await link.recv(2, timeoutMs);
  if (a !== P.INSYNC) throw new Error(`Bootloader out of sync (got 0x${a.toString(16)}).`);
  if (b === P.INVALID) throw new Error("Bootloader rejected the command as invalid.");
  if (b === P.FAILED) throw new Error("Bootloader reported the command failed.");
  if (b === P.BAD_SILICON_REV) throw new Error("Firmware refuses to run on this silicon revision.");
  if (b !== P.OK) throw new Error(`Unexpected bootloader reply 0x${b.toString(16)}.`);
}

async function command(link, bytes, timeoutMs = 2000) {
  await link.send([...bytes, P.EOC]);
  await expectSync(link, timeoutMs);
}

async function getInfo(link, param) {
  await link.send([P.GET_DEVICE, param, P.EOC]);
  const raw = await link.recv(4, 2000);
  await expectSync(link);
  return new DataView(raw.buffer, raw.byteOffset, 4).getUint32(0, true);
}

async function sync(link, attempts = 5) {
  let last;
  for (let i = 0; i < attempts; i += 1) {
    link.flush();
    try {
      await command(link, [P.GET_SYNC], 1000);
      return;
    } catch (e) {
      last = e;
      await sleep(200);
    }
  }
  throw new Error(
    "No response from a bootloader on this port. The bootloader only listens for " +
      "a few seconds after power-up: unplug the board, click Flash, then plug it " +
      `back in. (${last?.message || "no reply"})`,
  );
}

async function identify(link) {
  await sync(link);
  const blRev = await getInfo(link, INFO.BL_REV);
  if (blRev < 3 || blRev > 5) {
    throw new Error(`Unsupported bootloader revision ${blRev} (this tool needs 3-5).`);
  }
  return {
    blRev,
    boardId: await getInfo(link, INFO.BOARD_ID),
    boardRev: await getInfo(link, INFO.BOARD_REV),
    flashSize: await getInfo(link, INFO.FLASH_SIZE),
  };
}

/**
 * Read what board is on the other end without writing anything, so the user can
 * find out which firmware to download before committing to a flash. Leaves the
 * board sitting in its bootloader (we never send REBOOT), so a flash can follow.
 */
export async function readBoardInfo(port, { baudRate = 115200 } = {}) {
  const link = new Link(port);
  await link.open(baudRate);
  try {
    return await identify(link);
  } finally {
    await link.close();
  }
}

/**
 * Flash `fw` (from parseApj/parseBin) to the board behind `port`.
 * Refuses to write when the firmware's board ID does not match the board.
 */
export async function flash(
  port,
  fw,
  { onProgress = () => {}, onLog = () => {}, baudRate = 115200 } = {},
) {
  const link = new Link(port);
  await link.open(baudRate);
  try {
    onLog("Looking for a bootloader…");
    const { blRev, boardId, boardRev, flashSize } = await identify(link);
    onLog(`Bootloader rev ${blRev} · board ID ${boardId} rev ${boardRev} · ${flashSize} bytes flash`);

    // The single check that stops a mis-click from bricking someone's aircraft.
    if (fw.boardId == null) {
      onLog("WARNING: raw .bin carries no board ID — nothing verified the fit.");
    } else if (fw.boardId !== boardId) {
      throw new Error(
        `Refusing to flash: this firmware is built for board ID ${fw.boardId}, ` +
          `the connected board is ID ${boardId}.`,
      );
    }
    if (fw.image.length > flashSize) {
      throw new Error(
        `Refusing to flash: firmware is ${fw.image.length} bytes, the board has ${flashSize}.`,
      );
    }

    onLog("Erasing flash (this can take up to a minute — do not unplug)…");
    onProgress({ phase: "erase", percent: 0 });
    await command(link, [P.CHIP_ERASE], 60000);

    onLog(`Writing ${fw.image.length} bytes…`);
    for (let off = 0; off < fw.image.length; off += PROG_MULTI_MAX) {
      const chunk = fw.image.subarray(off, off + PROG_MULTI_MAX);
      await command(link, [P.PROG_MULTI, chunk.length, ...chunk], 5000);
      onProgress({ phase: "write", percent: ((off + chunk.length) / fw.image.length) * 100 });
    }

    onLog("Verifying…");
    onProgress({ phase: "verify", percent: 0 });
    await link.send([P.GET_CRC, P.EOC]);
    const raw = await link.recv(4, 30000);
    await expectSync(link);
    const reported = new DataView(raw.buffer, raw.byteOffset, 4).getUint32(0, true);
    const expected = firmwareCrc(fw.image, flashSize);
    if (reported !== expected) {
      throw new Error(
        `Verification failed (board 0x${reported.toString(16)} vs firmware ` +
          `0x${expected.toString(16)}). The board still holds a partial image — ` +
          "reflash before flying.",
      );
    }

    onLog("Verified. Rebooting.");
    await link.send([P.REBOOT, P.EOC]); // No reliable ack — it is already gone.
    onProgress({ phase: "done", percent: 100 });
  } finally {
    await link.close();
  }
}

// This repo has no working test runner, so the one check that matters rides
// along in dev the way panelTokens' palette check does. A broken CRC32 would
// otherwise only surface as "verification failed" after a real flash.
if (process.env.NODE_ENV !== "production") {
  const check = crc32(new TextEncoder().encode("123456789"));
  if (check !== 0xcbf43926) {
    console.error(`px4Bootloader: CRC32 is wrong (got 0x${check.toString(16)}, want 0xcbf43926)`);
  }
}
